import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { setPageMeta } from '../lib/seo';
import { useCurrency } from '../lib/useCurrency';
import { getCurrencySymbol } from '../lib/currency';

function Payment() {
  const [user, setUser] = useState(null);
  const [credits, setCredits] = useState(0);
  const [customAmount, setCustomAmount] = useState(null);
  const [selectedBaseAmount, setSelectedBaseAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const { currency, setCurrency, formatFromEur, formatAmount, convertFromEur } = useCurrency();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setCredits(parseFloat(parsedUser.credits) || 0);
    }
    fetchPackages();
    setPageMeta({
      title: 'Payments | Clouniq',
      description: 'Top up balance, track transactions, and manage payments securely.',
    });
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get('/api/payments/packages');
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const handlePackageSelect = (amount) => {
    setSelectedBaseAmount(amount);
    setCustomAmount(null);
    setCredits(convertFromEur(amount));
  };

  const handleCustomAmount = (value) => {
    setSelectedBaseAmount(null);
    setCredits(value);
    setCustomAmount(value);
  };

  const handlePayment = async () => {
    if (!user) {
      message.error('Please login first');
      return;
    }

    if (!credits || credits <= 0) {
      message.error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/payments/create-payment', {
        userId: user.id,
        amount: credits,
        currency,
        description: 'CLOUNIQ Credits Top-up',
        frontOrigin: window.location.origin,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      message.error(error.response?.data?.error || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [5, 10, 25, 50, 100];

  useEffect(() => {
    if (selectedBaseAmount !== null && customAmount === null) {
      setCredits(convertFromEur(selectedBaseAmount));
    }
  }, [currency, selectedBaseAmount, customAmount, convertFromEur]);

  return (
    <section className="payment-section xp-payment-page">
      <div className="container">
        <div className="xp-payment-layout">
          <div className="payment-card">
            <div className="payment-header">
              <h2 className="payment-title">Top up account balance</h2>
              <p className="payment-subtitle">
                Current balance: <span className="balance-amount">{formatAmount(credits)}</span>
              </p>
            </div>

            <div className="currency-switch">
              <span className="currency-label">Currency</span>
              <div className="currency-buttons">
                {['EUR', 'GBP'].map((code) => (
                  <button
                    key={code}
                    className={`currency-btn ${currency === code ? 'active' : ''}`}
                    onClick={() => setCurrency(code)}
                    type="button"
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>

            <div className="quick-amounts">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  className={`quick-amount-btn ${customAmount === null && selectedBaseAmount === amount ? 'active' : ''}`}
                  onClick={() => handlePackageSelect(amount)}
                >
                  +{formatFromEur(amount)}
                </button>
              ))}
            </div>

            <div className="custom-amount-section">
              <label className="form-label">Or enter custom amount</label>
              <div className="custom-amount-input">
                <span className="currency-symbol">{getCurrencySymbol(currency)}</span>
                <input
                  type="number"
                  className="form-control"
                  placeholder="0.00"
                  min="1"
                  step="0.01"
                  value={customAmount || ''}
                  onChange={(e) => handleCustomAmount(parseFloat(e.target.value) || null)}
                />
              </div>
            </div>

            <div className="selected-amount">
              <span className="label">Amount to add</span>
              <span className="value">{formatAmount(credits)}</span>
            </div>

            <button
              className="btn btn-primary btn-lg w-100 payment-btn"
              onClick={handlePayment}
              disabled={loading || !credits || credits <= 0}
            >
              {loading ? 'Processing...' : `Pay ${formatAmount(credits)}`}
            </button>

            <div className="payment-security">
              <span>Secure payment powered by Paynet</span>
            </div>
          </div>

          {packages.length > 0 && (
            <div className="packages-card">
              <h3 className="packages-title">Popular packages</h3>
              <div className="packages-grid">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    className={`package-item ${pkg.isPopular ? 'popular' : ''}`}
                    onClick={() => handlePackageSelect(parseFloat(pkg.price))}
                  >
                    {pkg.isPopular && <span className="package-badge">Popular</span>}
                    <div className="package-coins">{pkg.coins} coins</div>
                    <div className="package-price">{formatFromEur(pkg.price)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default Payment;



